import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { env } from '../../environments/environment';
import {
  Answer,
  Inspection,
  InspectionVersion,
  Inspector,
  ListFilters,
  ListResults,
  RecLetter,
  SentEmail,
  UnderwritingStatus,
  Vendor,
} from '../shared/interfaces';
import { InspectionStatus } from './inspection.status';

@Injectable({
  providedIn: 'root',
})
export class InspectionService {
  constructor(protected http: HttpClient) {}

  list(filters?: ListFilters) {
    return this.http.get<ListResults<Inspection>>(`${env.APIUrl}/inspection`, { params: filters as any }).toPromise();
  }

  flow() {
    return this.http.get<{ [key: string]: Inspection[] }>(`${env.APIUrl}/inspection/flow`).toPromise();
  }

  waiting(filters?: ListFilters) {
    return this.http
      .get<ListResults<Inspection>>(`${env.APIUrl}/inspection/waiting`, { params: filters as any })
      .toPromise();
  }

  listSyncing(filters?: ListFilters) {
    return this.http
      .get<ListResults<Inspection>>(`${env.APIUrl}/inspection/syncing`, { params: filters as any })
      .toPromise();
  }

  get(id: number) {
    return this.http.get<Inspection>(`${env.APIUrl}/inspection/${id}`).toPromise();
  }

  archived(archived: boolean, filters?: ListFilters) {
    return this.http
      .get<ListResults<Inspection>>(`${env.APIUrl}/inspection/archived`, {
        params: {
          archived: archived,
          ...filters,
        } as any,
      })
      .toPromise();
  }

  changes(inspectionId: number) {
    return this.http.get<any[]>(`${env.APIUrl}/inspection/${inspectionId}/changes`).toPromise();
  }

  edit(id: number, inspection: Inspection) {
    return this.http.put<Inspection>(`${env.APIUrl}/inspection/${id}`, inspection).toPromise();
  }

  setAssignment(inspectionId: number, assigneeId) {
    return this.http
      .post<Inspection>(`${env.APIUrl}/inspection/${inspectionId}/assignInspection`, { assigneeId })
      .toPromise();
  }

  accept(id: number, rating: number, ratingNote: string) {
    return this.http
      .put<Inspection>(`${env.APIUrl}/inspection/${id}/accept/${rating}`, { ratingNote: ratingNote })
      .toPromise();
  }

  reopen(id: number) {
    return this.http.put<Inspection>(`${env.APIUrl}/inspection/${id}/reopen`, null).toPromise();
  }

  reject(id: number) {
    return this.http.put<Inspection>(`${env.APIUrl}/inspection/${id}/reject`, null).toPromise();
  }

  archive(id: number) {
    return this.http.put<Inspection>(`${env.APIUrl}/inspection/${id}/archive`, null).toPromise();
  }

  restore(id: number) {
    return this.http.put<Inspection>(`${env.APIUrl}/inspection/${id}/restore`, null).toPromise();
  }

  create(inspection: Inspection) {
    return this.http.post<Inspection>(`${env.APIUrl}/inspection`, inspection).toPromise();
  }

  sendReminderVendorAssign(id: number) {
    return this.http.put<any>(`${env.APIUrl}/inspection/${id}/reminderVendorAssign`, null).toPromise();
  }

  sendUnsuccessfulContactEmail(id: number) {
    return this.http.put<any>(`${env.APIUrl}/inspection/${id}/sendUnsuccessfulContactEmail`, null).toPromise();
  }

  sendFinalUnsuccessfulContactEmail(id: number) {
    return this.http.put<any>(`${env.APIUrl}/inspection/${id}/sendFinalUnsuccessfulContactEmail`, null).toPromise();
  }

  sendInspectionReassignedEmail(id: number) {
    return this.http.put<any>(`${env.APIUrl}/inspection/${id}/sendInspectionReassignedEmail`, null).toPromise();
  }

  sendReminderVendorDue(id: number) {
    return this.http.put<any>(`${env.APIUrl}/inspection/${id}/reminderVendorDue`, null).toPromise();
  }

  sendReminderVendorRejected(id: number) {
    return this.http.put<any>(`${env.APIUrl}/inspection/${id}/reminderVendorRejected`, null).toPromise();
  }

  sendReminderToInspectorDue(id: number) {
    return this.http.put<any>(`${env.APIUrl}/inspection/${id}/reminderInspectorDue`, null).toPromise();
  }

  sendReminderToInspectorRejected(id: number) {
    return this.http.put<any>(`${env.APIUrl}/inspection/${id}/reminderInspectorRejected`, null).toPromise();
  }

  assignVendor(id: number, vendor: Vendor) {
    return this.http.put<any>(`${env.APIUrl}/inspection/${id}/vendor`, vendor).toPromise();
  }

  assignInspector(id: number, inspector: Inspector) {
    return this.http.put<any>(`${env.APIUrl}/inspection/${id}/inspector`, inspector).toPromise();
  }

  notifyBrokerAndCustomerInspectorAssigned(id: number) {
    return this.http
      .put<any>(`${env.APIUrl}/inspection/${id}/notifyBrokerAndCustomerInspectorAssigned`, null)
      .toPromise();
  }

  addDate(id: number, newDate: Partial<Inspection>) {
    return this.http.put<any>(`${env.APIUrl}/inspection/${id}/dueDate`, newDate).toPromise();
  }

  setDeadlines(id: number, newDates) {
    return this.http.put<any>(`${env.APIUrl}/inspection/${id}/deadlines`, newDates).toPromise();
  }

  remove(id: number) {
    return this.http.delete<Inspection>(`${env.APIUrl}/inspection/${id}`).toPromise();
  }

  answers(id: number) {
    return this.http.get<any[]>(`${env.APIUrl}/inspection/${id}/answers`).toPromise();
  }

  validate(id: number) {
    return this.http.post<Inspection>(`${env.APIUrl}/inspection/${id}/validate`, null).toPromise();
  }

  generateReport(id: number, params) {
    return this.http.post<any>(`${env.APIUrl}/inspection/${id}/triggerReport`, params).toPromise();
  }

  listReports(id: number) {
    return this.http.get<any>(`${env.APIUrl}/inspection/${id}/reports`).toPromise();
  }

  generateLetter(id: number, selectedUser, preview = false) {
    return this.http
      .post<any>(`${env.APIUrl}/recommendationLetter/fromInspection/${id}`, { id: selectedUser, preview: preview })
      .toPromise();
  }

  generateNoIssuesLetter(id: number, selectedUser, preview = false) {
    return this.http
      .post<any>(`${env.APIUrl}/recommendationLetter/noIssuesFromInspection/${id}`, {
        id: selectedUser,
        preview: preview,
      })
      .toPromise();
  }

  sendRecommendationLetterEmail(
    to: string[],
    cc: string[],
    subject: string,
    customerUrl: string,
    customField: string,
    inspectionId: number,
    letterId: number,
  ) {
    const payload = {
      to: to,
      cc: cc,
      subject: subject,
      customerUrl: customerUrl,
      customField: customField,
      inspectionId: inspectionId,
      letterId: letterId,
    };
    return this.http.post<any>(`${env.APIUrl}/recommendationLetter/sendEmail`, payload).toPromise();
  }

  listLetters(id: number) {
    return this.http.get<any>(`${env.APIUrl}/inspection/${id}/letters`).toPromise();
  }

  getLatestReminderEmail(id: number) {
    return this.http.get<SentEmail | null>(`${env.APIUrl}/inspection/${id}/latestReminderEmail`).toPromise();
  }

  setUnderwritingStatus(id: number, status: UnderwritingStatus) {
    return this.http.put<Inspection>(`${env.APIUrl}/inspection/${id}/underwritingStatus/${status}`, null).toPromise();
  }

  underwritingOpen(filters?: ListFilters) {
    return this.http
      .get<Inspection[]>(`${env.APIUrl}/inspection/underwritingOpen`, { params: filters as any })
      .toPromise();
  }

  underwritingClosed(filters?: ListFilters) {
    return this.http
      .get<ListResults<Inspection>>(`${env.APIUrl}/inspection/underwritingClosed`, { params: filters as any })
      .toPromise();
  }

  underwritingInprogress(filters?: ListFilters) {
    return this.http
      .get<ListResults<Inspection>>(`${env.APIUrl}/inspection/underwritingInProgress`, { params: filters as any })
      .toPromise();
  }

  getInspectionByGalleryHash(hash: string) {
    const encodedHash = encodeURIComponent(hash || '');
    return this.http.get<Inspection>(`${env.APIUrl}/inspection/by-gallery-token/${encodedHash}`).toPromise();
  }

  getAnswersByGalleryHash(hash: string) {
    const encodedHash = encodeURIComponent(hash || '');
    return this.http.get<Answer[]>(`${env.APIUrl}/inspection/by-gallery-token/${encodedHash}/answers`).toPromise();
  }

  getTreeByGalleryToken(token: string): Promise<InspectionVersion> {
    const encoded = encodeURIComponent(token || '');
    return this.http.get<InspectionVersion>(`${env.APIUrl}/inspection/by-gallery-token/${encoded}/tree`).toPromise();
  }

  getRecommendationLetterData(hash: string, skipRegisteringView = false, versionId = null) {
    // tslint:disable:no-unused-expression
    const params = new URLSearchParams();
    skipRegisteringView && params.append('skipRegisteringView', 'true');
    versionId && params.append('versionId', versionId);

    return this.http.get<RecLetter>(`${env.APIUrl}/recommendationLetter/${hash}?${params.toString()}`).toPromise();
  }

  async downloadReport(report) {
    const params = { key: report.s3Key };
    const s3url = await this.http.get<any>(`${env.APIUrl}/inspection/reportUrl`, { params }).toPromise();
    window.open(s3url);
  }

  public async downloadSummaryCsv() {
    const blob = await this.http.get(`${env.APIUrl}/inspection/summaryReport`, { responseType: 'blob' }).toPromise();
    const url = URL.createObjectURL(blob);
    const anchor: any = document.createElement('a');
    document.body.appendChild(anchor);
    anchor.style = 'display: none';
    anchor.href = url;
    anchor.download = `summary.csv`;
    anchor.click();
    document.body.removeChild(anchor);

    // give it enough time to work and don't break the url
    setTimeout(() => {
      URL.revokeObjectURL(url);
    }, 1000);
  }

  public async downloadUnderwrittingCsv() {
    const blob = await this.http
      .get(`${env.APIUrl}/inspection/underwrittingReport`, { responseType: 'blob' })
      .toPromise();
    const url = URL.createObjectURL(blob);
    const anchor: any = document.createElement('a');
    document.body.appendChild(anchor);
    anchor.style = 'display: none';
    anchor.href = url;
    anchor.download = `underwritting.csv`;
    anchor.click();
    document.body.removeChild(anchor);

    // give it enough time to work and don't break the url
    setTimeout(() => {
      URL.revokeObjectURL(url);
    }, 1000);
  }

  public async downloadUnderwrittingInProgressCsv() {
    const blob = await this.http
      .get(`${env.APIUrl}/inspection/underwrittingInProgressReport`, { responseType: 'blob' })
      .toPromise();
    const url = URL.createObjectURL(blob);
    const anchor: any = document.createElement('a');
    document.body.appendChild(anchor);
    anchor.style = 'display: none';
    anchor.href = url;
    anchor.download = `underwrittingInProgressReport.csv`;
    anchor.click();
    document.body.removeChild(anchor);

    // give it enough time to work and don't break the url
    setTimeout(() => {
      URL.revokeObjectURL(url);
    }, 1000);
  }

  public setupInspectionFolder(id: number) {
    return this.http.post<Inspection>(`${env.APIUrl}/inspection/${id}/setupFolder`, null).toPromise();
  }

  async exportCsv(filters: ListFilters) {
    const blob = await this.http
      .get(`${env.APIUrl}/inspection/exportCsv`, { params: filters as any, responseType: 'blob' })
      .toPromise();
    console.log('blob', blob);
    const url = URL.createObjectURL(blob);
    const anchor: any = document.createElement('a');
    document.body.appendChild(anchor);
    anchor.style = 'display: none';
    anchor.href = url;
    anchor.download = `filtered-inspections.csv`;
    anchor.click();
    document.body.removeChild(anchor);

    // give it enough time to work and don't break the url
    setTimeout(() => {
      URL.revokeObjectURL(url);
    }, 1000);
  }

  // Follow same logic from the inspection flow screen
  statusAsText(inspection: Inspection) {
    const plus2weeks = new Date();
    plus2weeks.setDate(plus2weeks.getDate() + 14);

    const overdue = inspection.dueDate && new Date(inspection.dueDate) < new Date();

    switch (inspection.status) {
      case InspectionStatus.NO_DATE_ASSIGNED:
        return 'No date assigned';

      case InspectionStatus.READY_TO_BE_ASSIGNED:
        return 'Ready to be assigned';

      case InspectionStatus.ASSIGNMENT_MADE:
      case InspectionStatus.WAITING:
        if (new Date(inspection.dueDate) && new Date(inspection.dueDate) < plus2weeks) {
          if (!inspection.vendor) {
            return 'Ready to be assigned';
          }

          if (overdue && inspection.inspector) {
            return 'Due completion';
          } else {
            return 'Assignment made';
          }
        } else {
          return 'Waiting';
        }

      case InspectionStatus.NEEDS_REVIEW:
      case InspectionStatus.INVALID_SYNC:
        return 'Completed and needs review';

      case InspectionStatus.REJECTED:
        return 'Rejected';
      case InspectionStatus.ACCEPTED:
        return 'Accepted';
      case InspectionStatus.DEPRECATED_COMPLETED:
        return 'Completed';
    }
  }

  getSentEmailById(id: number) {
    return this.http.get<SentEmail>(`${env.APIUrl}/sentEmails/${id}`).toPromise();
  }

  downloadSentEmail(id: number) {
    return this.http
      .post(`${env.APIUrl}/sentEmails/${id}/download`, {}, { responseType: 'blob' as 'json' })
      .toPromise();
  }

  uploadPdf(inspectionId: number, formData: FormData) {
    return this.http.post(`${env.APIUrl}/inspection/${inspectionId}/parsePdf`, formData).toPromise();
  }

  getNotes(inspectionId: number) {
    return this.http.get<any[]>(`${env.APIUrl}/inspection/${inspectionId}/notes`).toPromise();
  }

  createNote(inspectionId: number, noteData: { reason: string; content: string }) {
    return this.http.post<any>(`${env.APIUrl}/inspection/${inspectionId}/notes`, noteData).toPromise();
  }

  updateNote(inspectionId: number, noteId: number, noteData: { reason: string; content: string }) {
    return this.http.put<any>(`${env.APIUrl}/inspection/${inspectionId}/notes/${noteId}`, noteData).toPromise();
  }

  deleteNote(inspectionId: number, noteId: number) {
    return this.http.delete<any>(`${env.APIUrl}/inspection/${inspectionId}/notes/${noteId}`).toPromise();
  }

  getWithMarkupsToken(inspectionId: number) {
    return this.http.put<string>(`${env.APIUrl}/inspection/${inspectionId}/galleryWithMarkupToken`, null).toPromise();
  }

  deleteWithMarkupsToken(inspectionId: number) {
    return this.http.delete<string>(`${env.APIUrl}/inspection/${inspectionId}/galleryWithMarkupToken`).toPromise();
  }

  getWithoutMarkupsToken(inspectionId: number) {
    return this.http
      .put<string>(`${env.APIUrl}/inspection/${inspectionId}/galleryWithoutMarkupToken`, null)
      .toPromise();
  }

  deleteWithoutMarkupsToken(inspectionId: number) {
    return this.http.delete<string>(`${env.APIUrl}/inspection/${inspectionId}/galleryWithoutMarkupToken`).toPromise();
  }
}
